import { Form, Input, message, InputNumber } from "antd";
import { useState, useEffect } from "react";

import FormItemList from "./components/FormItemList";
import type { StoryTimeDetailType } from "./content/storyTimeType"
import { header } from "../utils/zippyAdminHeader";
import ContentSection from "../../../components/layout/mainContent/ContentSection";
import MainContentFooter from "../../../components/layout/mainContent/MainContentFooter";
import { Colors } from "../../../constants/Colors";
import { apiCall } from "../../../utils/apiCall";
import requiredRule from "../../../utils/formRules/requiredRule";

interface StoryTimeDetailLearningProps {
  storyGroupId: any;
  detailInfo: StoryTimeDetailType | undefined;
  learningForm: any;
  userName: string | null | undefined;
}

const textStyle: React.CSSProperties = {
  color: Colors.notEditableText,
  lineHeight: "22px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  margin: 0,
  padding: '4px 0',
};

const StoryTimeDetailLearning = ({
  storyGroupId,
  detailInfo,
  learningForm,
  userName,
}: StoryTimeDetailLearningProps) => {
  const form = learningForm;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    setIsLoading(true);
    try {
      const learningGoals = (values.learningGoals || [])
        .map((x: string) => (x || '').trim())
        .filter((x: string) => x !== '');

      const keyPhrases = (values.keyPhrases || [])
        .map((x: string) => (x || '').trim())
        .filter((x: string) => x !== '');

      const payload = {
        estimatedReadMinutes: values.estimatedReadMinutes,
        operator: userName,
        ...(learningGoals.length > 0 ? { learningGoals: learningGoals } : {}),
        ...(values.words ? { words: values.words.split(',').map((item: string) => item.trim()) } : {}),
        ...(keyPhrases.length > 0 ? { keyPhrases: keyPhrases } : {}),
      };

      const data = await apiCall({
        method: "PATCH",
        header: header,
        endPoint:
          process.env.REACT_APP_ZIPPY_ADMIN +
          `/stories/admin/groups/${storyGroupId}/content`,
        payload: payload
      });

      if (data && data.status) {
        message.success(data.status);
      } else {
        message.error(data.response.data.status);
      }
    } catch (e) {
      console.log(e);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (detailInfo) {
      form.setFieldsValue({
        estimatedReadMinutes: detailInfo.content.estimatedReadMinutes,
        learningGoals: detailInfo.content.learningGoals,
        words: detailInfo.content.words.join(", "),
        keyPhrases: detailInfo.content.keyPhrases,
      });
    }
  }, [detailInfo, form]);

  return (
    <ContentSection title="" showTitle={false}>
      <Form
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 14 }}
        onFinish={handleSubmit}
      >
        <Form.Item label="Story title">
          <p style={textStyle}>{detailInfo?.title}</p>
        </Form.Item>
        <Form.Item label="CAP">
          <p style={textStyle}>{Array.isArray(detailInfo?.caps) ? detailInfo?.caps.join(', ') : ''}</p>
        </Form.Item>
        <Form.Item
          label="Estimated reading time"
          required={true}
          extra="Auto-filled based on audio length; can be adjusted manually."
        >
          <Form.Item
            name="estimatedReadMinutes"
            rules={[requiredRule]}
            noStyle
          >
            <InputNumber controls={false} precision={0} style={{ width: 39, marginRight: 12 }} />
          </Form.Item>
          <span>minutes</span>
        </Form.Item>
        <FormItemList
          label="Learning goals"
          name="learningGoals"
        />
        <Form.Item
          label="Words"
          name="words"
          extra="Separate words with commas"
        >
          <Input />
        </Form.Item>
        <FormItemList
          label="Key phrases"
          name="keyPhrases"
        />
        <MainContentFooter
          isLoading={isLoading}
          backTo="/zippy-and-me/story-time"
          btnText="Save"
        />
      </Form>
    </ContentSection>
  );
};

export default StoryTimeDetailLearning;
